// Standalone runtime env resolution.
//
// This client is a global login-redirect page (login-redirect.{env}.floqast.{tld})
// presenting a static choice of which FloQast region to log into. The two
// destination URLs (REACT_APP_US_LOGIN_URL / REACT_APP_EMEA_LOGIN_URL) are a
// pure function of the deploy environment, not arbitrary business config —
// see getDerivedLoginUrls() — so they're computed here instead of depending
// on an env-config CDN payload being complete. The CDN payload is still
// checked first (production source of truth if these URLs ever need to move
// independently of environment identity), with the derived value as an
// always-correct safety net. This keeps the bundle promotable across deploy
// environments (build once in automation, promote to production unchanged).
//
// Mirrors ui/customer-onboarding-client/src/runtime-config.ts (this repo),
// adapted for this client's `login-redirect` hostname prefix.

import { deriveEnvAndTldFromHostname } from '@floqastinc/fq-url-source/client';

const DEFAULT_TLD = 'floqast.app';
const CLIENT_SUBDOMAIN = 'login-redirect';

// env-config's CDN alias lives on floqast.app for every environment except
// the fq* dev/test tier, even though automation/automation-eu serve this
// page itself from floqast.engineering. Confirmed via DNS: env-config.automation
// .floqast.engineering does not resolve; env-config.automation.floqast.app does.
const ENV_CONFIG_TLD_OVERRIDES = {
    automation: 'floqast.app',
    'automation-eu': 'floqast.app',
};

let envConfigCache = null;
let envConfigPromise = null;
let envConfigActivated = false;

export function getHostnameTld() {
    const hostname =
        (typeof window !== 'undefined' && window.location?.hostname) || '';
    const match = hostname.match(/\.(floqast\.(?:engineering|app|com|ai))$/);
    if (match) return match[1];
    if (
        (hostname === 'localhost' || hostname === '127.0.0.1') &&
        process.env.REACT_APP_FQ_ENV
    ) {
        // fq envs are on .engineering; CDN URL must match or fetch will fail
        return 'floqast.engineering';
    }
    return DEFAULT_TLD;
}

// Derives the deploy env from the standalone hostname. Localhost reads
// REACT_APP_FQ_ENV (exported by the dev start script).
//   login-redirect.floqast.app                    -> us (prod)
//   login-redirect.automation.floqast.engineering -> automation
//   login-redirect.fq1.floqast.engineering        -> fq1
export function detectEnvironment() {
    if (typeof window === 'undefined') return null;
    const hostname = window.location?.hostname;
    if (!hostname) return null;

    if (hostname === 'localhost' || hostname === '127.0.0.1') {
        return process.env.REACT_APP_FQ_ENV || null;
    }

    // Strip this client's subdomain prefix before handing off to the shared
    // parser. Bare 'login-redirect.floqast.<tld>' (US prod, no env segment)
    // maps to 'www' so the library normalises it to 'us'.
    const strippedHostname = /^login-redirect\.floqast\./.test(hostname)
        ? hostname.replace('login-redirect.', 'www.')
        : hostname.replace(new RegExp(`^${CLIENT_SUBDOMAIN}[.-]`), '');

    const { env } = deriveEnvAndTldFromHostname(strippedHostname);
    return env ?? null;
}

export function getEnvConfigUrl(env) {
    const tld = ENV_CONFIG_TLD_OVERRIDES[env] ?? getHostnameTld();
    if (env === 'us') return `https://env-config.${tld}`;
    return `https://env-config.${env}.${tld}`;
}

// Derives the two region login URLs directly from the detected environment.
// Mirrors the values previously baked per-env in .env.production/.env.automation/
// .env.dev-like — a global login page always redirects to the same US/EMEA
// targets for a given deploy tier, regardless of which region serves the page.
export function getDerivedLoginUrls(env) {
    if (!env) return {};
    if (env === 'us' || env === 'eu') {
        return {
            REACT_APP_US_LOGIN_URL: 'https://floqast.app/login',
            REACT_APP_EMEA_LOGIN_URL: 'https://eu.floqast.app/login',
        };
    }
    if (env === 'automation' || env === 'automation-eu') {
        return {
            REACT_APP_US_LOGIN_URL: 'https://automation.floqast.app/login',
            REACT_APP_EMEA_LOGIN_URL: 'https://automation-eu.floqast.app/login',
        };
    }
    // fq envs / dev — a single shared target for both regions, matching
    // .env.dev-like (fq envs aren't region-split).
    const url = `https://${env}.floqast.engineering/login`;
    return { REACT_APP_US_LOGIN_URL: url, REACT_APP_EMEA_LOGIN_URL: url };
}

// Fetches the env-config payload from the CDN once, caches it, and activates
// getEnv() to serve from it. Idempotent.
export function fetchEnvConfig() {
    if (envConfigCache) return Promise.resolve(envConfigCache);
    if (envConfigPromise) return envConfigPromise;
    envConfigPromise = fetchEnvConfigImpl();
    return envConfigPromise;
}

async function fetchEnvConfigImpl() {
    const env = detectEnvironment();
    if (!env) {
        console.warn(
            '[runtime-config] could not detect environment; skipping env-config fetch',
        );
        envConfigCache = {};
        envConfigActivated = true;
        return envConfigCache;
    }
    try {
        const url = getEnvConfigUrl(env);
        // Use XMLHttpRequest, not fetch — fq-intl installs a global fetch wrapper
        // that injects an x-fq-intl-vars header, triggering a CORS preflight that
        // CloudFront rejects with 403. XHR bypasses that wrapper.
        const responseText = await new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            xhr.open('GET', url, true);
            xhr.withCredentials = false;
            xhr.timeout = 5000;
            xhr.ontimeout = () =>
                reject(new Error('env-config request timed out'));
            xhr.onload = () =>
                xhr.status >= 200 && xhr.status < 300
                    ? resolve(xhr.responseText)
                    : reject(new Error(`${xhr.status} ${xhr.statusText}`));
            xhr.onerror = () => reject(new Error('Network error'));
            xhr.send();
        });
        const data = JSON.parse(responseText);
        // Payload is wrapped as { environment, ..., config: { KEY: value } }.
        envConfigCache = data.config || data;
        envConfigActivated = true;
        return envConfigCache;
    } catch (err) {
        console.warn(`[runtime-config] env-config fetch failed: ${err.message}`);
        envConfigCache = {};
        envConfigActivated = true;
        return envConfigCache;
    }
}

// Runtime env reader. Resolution order:
//   1. env-config CDN payload (production source of truth), once activated
//   2. derived login URLs (always-correct safety net — see getDerivedLoginUrls)
//   3. build-time / localhost process.env
export function getEnv(key) {
    if (
        envConfigActivated &&
        envConfigCache &&
        envConfigCache[key] != null &&
        envConfigCache[key] !== ''
    ) {
        return envConfigCache[key];
    }

    const hostname =
        (typeof window !== 'undefined' && window.location?.hostname) || '';
    const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';

    if (!isLocalhost) {
        const derived = getDerivedLoginUrls(detectEnvironment());
        if (derived[key] !== undefined) return derived[key];
    }

    try {
        if (process.env[key] !== undefined) {
            return process.env[key];
        }
    } catch {
        // process.env not available (non-CRA environment without polyfill)
    }
    return undefined;
}
